"use client";

import { motion } from "framer-motion";
import {
  Sparkles,
  MessageSquare,
  Globe,
  Building2,
  BarChart3,
} from "lucide-react";
import RemotionPlayer from "./RemotionPlayer";
import { SeoReplyAnimation } from "@/remotion/homepage/SeoReplyAnimation";

const features = [
  {
    title: "AI-Powered Intelligence",
    desc: "GPT-4o-mini generates professional responses that match your brand's unique voice and tone.",
    icon: <Sparkles className="h-7 w-7 text-brand-600" />,
    className:
      "md:col-span-2 md:row-span-2 bg-gradient-to-br from-brand-50 to-white",
    illustration: (
      <div className="mt-6">
        <RemotionPlayer
          component={SeoReplyAnimation}
          durationInFrames={160}
          compositionWidth={600}
          compositionHeight={500}
        />
      </div>
    ),
  },
  {
    title: "Email & Mobile Alerts",
    desc: "Get review alerts where work already happens. Email is always available, with WhatsApp globally and Direct Text in the US/Canada.",
    icon: <MessageSquare className="h-6 w-6 text-accent-600" />,
    className: "md:col-span-1 md:row-span-1 bg-accent-50/40",
  },
  {
    title: "Global Reach",
    desc: "Auto-detects and replies in 50+ languages natively.",
    icon: <Globe className="h-6 w-6 text-brand-600" />,
    className: "md:col-span-1 md:row-span-1 bg-ink-50",
  },
  {
    title: "Multi-Location",
    desc: "Manage 1 or 100 locations with ease from a single dashboard.",
    icon: <Building2 className="h-6 w-6 text-brand-700" />,
    className: "md:col-span-1 md:row-span-2 bg-white",
    illustration: (
      <div className="mt-6 space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-lg border border-ink-100 bg-ink-50/60 p-2"
          >
            <div className="flex h-6 w-6 items-center justify-center rounded bg-brand-100 text-[10px] font-bold text-brand-700">
              L{i}
            </div>
            <div className="h-1.5 w-20 rounded-full bg-ink-200" />
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Real-time Analytics",
    desc: "Track sentiment, response times, and review growth effortlessly.",
    icon: <BarChart3 className="h-6 w-6 text-sig-amber" />,
    className: "md:col-span-1 md:row-span-1 bg-ink-50",
  },
];

export default function Advantage() {
  return (
    <section className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-14 max-w-2xl">
          <p className="ribbon mb-3 text-ink-500">The Advantage</p>
          <h2 className="font-display text-4xl font-bold tracking-tight text-ink-950 sm:text-5xl text-balance">
            Speed{" "}
            <em className="font-display italic font-normal text-brand-600">
              equals
            </em>{" "}
            growth.
          </h2>
          <p className="mt-4 text-lg text-ink-600 text-pretty">
            Built to be the fastest way to respond to Google reviews. Period.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {features.map((feature, idx) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              whileHover={{ y: -4 }}
              className={`relative overflow-hidden rounded-2xl border border-ink-100 p-7 shadow-sm transition-all hover:shadow-md ${feature.className}`}
            >
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-ink-950 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm leading-relaxed text-ink-600">
                {feature.desc}
              </p>
              {"illustration" in feature && feature.illustration}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
